import { Dispatch, FC, useState } from "react";
import { ReportElementInterface } from "../../../middleware/interfaces/report/report-element-interface";
import {
  StopEventPropagation,
  UpdateUserLastActionDate,
} from "../../../helper/ui/methods";
import { UserInterface } from "../../../middleware/interfaces/user/user-interface";

interface reportElementProps {
  user: UserInterface;
  reportElement: ReportElementInterface;
  selectedReportElement: ReportElementInterface | null;
  setSelectedReportElement: Dispatch<ReportElementInterface | null>;
  setReportElementNoteDialogReveal: Dispatch<boolean>;
  filter: boolean;
  dispatch: Dispatch<any>;
}

const ReportElement: FC<reportElementProps> = ({
  user,
  reportElement,
  selectedReportElement,
  setSelectedReportElement,
  setReportElementNoteDialogReveal,
  filter,
  dispatch,
}) => {
  const [reportImageMaximize, setReportImageMaximize] = useState(false);
  const [reportDataMaximize, setReportDataMaximize] = useState(false);

  const replacements = ["|", ":"];

  const isSelected =
    selectedReportElement?.reportElementID === reportElement.reportElementID;

  // the element's colour lives in the header swatch, so the clash image is
  // free to carry the eye instead of competing with a full-height fill
  const SetReportElementStyle = () => {
    return {
      "--reportElementColor": reportElement.reportElementColor.startsWith("#")
        ? reportElement.reportElementColor
        : "#" + reportElement.reportElementColor,
    } as React.CSSProperties;
  };

  const SetReportElementImageStyle = () => {
    if (reportImageMaximize) {
      return {
        "--reportElementImageWidth": "100%",
      } as React.CSSProperties;
    } else {
      return {
        "--reportElementImageWidth": "50%",
      } as React.CSSProperties;
    }
  };

  const SetReportElementDataStyle = () => {
    if (reportDataMaximize) {
      return {
        "--reportElementDataWidth": "100%",
      } as React.CSSProperties;
    } else {
      return {
        "--reportElementDataWidth": "50%",
      } as React.CSSProperties;
    }
  };

  const HandleReportElementClick = (event: React.MouseEvent) => {
    StopEventPropagation(event);
    UpdateUserLastActionDate(user, dispatch);
  };

  const HandleReportElementDoubleClick = (event: React.MouseEvent) => {
    StopEventPropagation(event);
  };

  const HandleReportElementImageDoubleClick = () => {
    setReportImageMaximize(!reportImageMaximize);
  };

  const HandleReportElementDataDoubleClick = () => {
    setReportDataMaximize(!reportDataMaximize);
  };

  const HandleReportElementHeaderClick = () => {
    setSelectedReportElement(reportElement);
  };

  const HandleReportElementHeaderDoubleClick = () => {
    setReportElementNoteDialogReveal(true);
  };

  const RenderReportElementLocation = () =>
    reportElement.reportElementComments
      .split("<br>")
      .map((comment, index) => {
        replacements.forEach((replacement) => {
          comment = comment.replace("/--/", replacement);
        });
        return comment.trim() !== "" ? (
          <span key={index} className="reportElementFieldValue">
            {comment}
          </span>
        ) : (
          <></>
        );
      });

  const RenderReportElementReview = () =>
    reportElement.reportElementReviewTexts
      .split("<br>")
      .map((reviewText, index) =>
        reviewText.trim() !== "" ? (
          <span key={index} className="reportElementFieldValue">
            {reviewText}
          </span>
        ) : (
          <></>
        )
      );

  const hasLocation =
    reportElement.reportElementComments &&
    reportElement.reportElementComments.trim() !== "";

  const hasReview =
    reportElement.reportElementReviewTexts &&
    reportElement.reportElementReviewTexts.trim() !== "";

  return (
    <>
      {!filter ? (
        <></>
      ) : (
        <div
          className={
            isSelected ? "reportElement reportElementSelected" : "reportElement"
          }
          key={reportElement.reportElementID}
          style={SetReportElementStyle()}
          onClick={HandleReportElementClick}
          onDoubleClick={HandleReportElementDoubleClick}
        >
          {!reportImageMaximize && !reportDataMaximize && (
            <div
              className="reportElementHeader"
              onClick={HandleReportElementHeaderClick}
              onDoubleClick={HandleReportElementHeaderDoubleClick}
            >
              <span className="reportElementSwatch"></span>
              <span className="reportElementName">
                {reportElement.reportElementName}
              </span>
            </div>
          )}
          <div className="reportElementBody">
            {!reportDataMaximize && (
              <div
                className="reportElementImage"
                onDoubleClick={HandleReportElementImageDoubleClick}
                style={SetReportElementImageStyle()}
              >
                <img
                  src={reportElement.reportElementImageUrl}
                  alt={`Report ${reportElement.reportElementID.toString()}`}
                />
              </div>
            )}
            {!reportImageMaximize && (
              <div
                className="reportElementData"
                onDoubleClick={HandleReportElementDataDoubleClick}
                style={SetReportElementDataStyle()}
              >
                <div className="reportElementField">
                  <span className="reportElementFieldLabel">LOCATION</span>
                  {hasLocation ? (
                    RenderReportElementLocation()
                  ) : (
                    <span className="reportElementFieldValue reportElementFieldEmpty">
                      Not recorded
                    </span>
                  )}
                </div>
                <div className="reportElementField">
                  <span className="reportElementFieldLabel">REVIEW</span>
                  {hasReview ? (
                    RenderReportElementReview()
                  ) : (
                    <span className="reportElementFieldValue reportElementFieldEmpty">
                      No comment
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default ReportElement;
